import { useCallback, useEffect, useRef, useState } from 'react'
import { contentRegion, focusFirstAlive } from '../../platform/a11y/focusFirstAlive'

/**
 * Focus recovery for a row-level quick pick (category or merchant) whose row, or whose pill, may leave.
 *
 * Overlay restores focus to the pill that opened it, and at close that pill still exists: the PATCH has
 * not even been sent. Overlay's fallbacks only cover an opener that is gone AT CLOSE, so they cannot see
 * the later case: the refetch removes the row, the focused pill goes with it, and the browser parks focus
 * on `<body>`. So after a pick the pill is watched. A commit that finds it detached while focus is on
 * `<body>` moves focus to the content region, the same last rung Overlay and ChromeLayout use. Focus the
 * user has moved elsewhere is left alone.
 *
 * **The pill can leave while its row stays.** "Needs merchant" is drawn only on a row with no merchant, so
 * a pick removes the pill from a row that remains (on any page not scoped to "no merchant"). Focus then
 * goes to that row's own link (the merchant name, which opens the transaction) rather than up to the
 * content region, so a keyboard user stays where they were. The row is found when the pick is made
 * (a grid `role="row"` or a card's `<li>`), because a detached pill no longer has ancestors to search.
 *
 * **The watch ends with the pick.** When the set settles (its refetch done), a state bump forces one more
 * commit of the host, and that render reads the refetched cache: either the row is gone by then
 * (recovered above) or it stayed, and the watch disarms. Left armed, a later sort or filter that removes
 * the row would pull focus to `<main>` with no pick behind it.
 *
 * Returns `watch(opener, done)`: call it at the pick with the opener and the set's promise.
 */
export function usePickedRowFocus(): (opener: HTMLElement | null, done: Promise<unknown>) => void {
  /** The pill of the last pick, until it is seen detached. */
  const watched = useRef<HTMLElement | null>(null)
  /** The watched pill's row, found at the pick, so a pill that leaves a staying row can hand focus to it. */
  const row = useRef<Element | null>(null)
  /** Set once the watched pick's set has settled; the next commit is the last that may recover. */
  const settled = useRef(false)
  /** Which pick is current, so an earlier pick settling cannot end a later one's watch. */
  const seq = useRef(0)
  const [, bump] = useState(0)

  // No dependency array on purpose: the row leaves on whichever commit renders the refetch,
  // and that commit is the host's (the list's data is read by the screen that owns this hook).
  useEffect(() => {
    const el = watched.current
    if (el === null) return
    if (document.contains(el)) {
      // The post-refetch commit found the row still here: nothing will take its focus away.
      if (settled.current) watched.current = null
      return
    }
    watched.current = null
    const active = document.activeElement
    if (active !== null && active !== document.body) return
    focusFirstAlive(row.current?.querySelector('a[href]'), contentRegion())
  })

  return useCallback((opener: HTMLElement | null, done: Promise<unknown>) => {
    watched.current = opener
    row.current = opener?.closest('[role="row"], li') ?? null
    settled.current = false
    const mine = ++seq.current
    void done.finally(() => {
      if (seq.current !== mine) return
      settled.current = true
      bump((n) => n + 1)
    })
  }, [])
}
